import React, { useState, useEffect } from "react";
import { X, Save, AlertCircle, RefreshCw } from "lucide-react";
import toast from "react-hot-toast";
import { OfflineOrderDetailsData } from "../../../Clinic/ClinicOrder/OfflineOrder/types";
import { OfflineItemService } from "../../../../../services/api/OfflineOrder/offline.service";
import {
  UpdateOfflineOrderPayload,
  UpdateOfflineProductItemInput,
} from "../../../../../services/api/OfflineOrder/types";
import defaultMedicineImg from "../../../../../assets/images/default_avatar.jpg";

interface EditProductState {
  productId: string;
  productName: string;
  productImage: string;
  tabletsPerStripe: number;
  productsItemsCount: number;
  inwardStockId: string;
  mrp: number | string;
  productDiscount: number | string;
  cgst: string | number;
  sgst: string | number;
}

interface EditOfflineOrderModalProps {
  isOpen: boolean;
  order: OfflineOrderDetailsData | null;
  onClose: () => void;
  onSuccess: (orderId: number | string) => void;
}

export const EditOfflineOrderModal: React.FC<EditOfflineOrderModalProps> = ({
  isOpen,
  order,
  onClose,
  onSuccess,
}) => {
  const [items, setItems] = useState<EditProductState[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (order && order.productDetails) {
      const initialItems: EditProductState[] = order.productDetails.map(
        (product) => ({
          productId: product.productId,
          productName: product.productName,
          productImage: product.productImage || defaultMedicineImg,
          tabletsPerStripe: product.tabletsPerStripe || 1,
          productsItemsCount: product.productsItemsCount || 1,
          inwardStockId: product.inwardStockId,
          mrp: Number(product.productMrp || 0),
          productDiscount: Number(product.sellingDiscount || 0),
          cgst: product.cgst || 0,
          sgst: product.sgst || 0,
        })
      );
      setItems(initialItems);
      setErrorMessage(null);
    }
  }, [order, isOpen]);

  if (!isOpen || !order) return null;

  const handlePriceChange = (index: number, val: string) => {
    setItems((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], mrp: val };
      return copy;
    });
  };

  const handleDiscountChange = (index: number, val: string) => {
    setItems((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], productDiscount: val };
      return copy;
    });
  };

  // Calculations
  const calculateTotals = () => {
    let totalMrpSum = 0;
    let totalDiscountSum = 0;
    let grandTotalSum = 0;

    items.forEach((item) => {
      const mrpNum = parseFloat(String(item.mrp)) || 0;
      const discountPercent = parseFloat(String(item.productDiscount)) || 0;
      const qty = item.productsItemsCount || 1;

      const itemTotalMrp = mrpNum * qty;
      const itemDiscountAmount = itemTotalMrp * (discountPercent / 100);
      const itemTotalAfterDiscount = itemTotalMrp - itemDiscountAmount;

      totalMrpSum += itemTotalMrp;
      totalDiscountSum += itemDiscountAmount;
      grandTotalSum += itemTotalAfterDiscount;
    });

    const decimalPart = grandTotalSum - Math.floor(grandTotalSum);
    let roundedGrandTotal: number;
    if (decimalPart >= 0.5) {
      roundedGrandTotal = Math.ceil(grandTotalSum);
    } else {
      roundedGrandTotal = Math.floor(grandTotalSum);
    }

    const roundOff = parseFloat(
      (roundedGrandTotal - grandTotalSum).toFixed(2)
    );

    return {
      totalMrp: totalMrpSum.toFixed(2),
      totalDiscount: totalDiscountSum.toFixed(2),
      roundOff: roundOff.toFixed(2),
      grandTotal: roundedGrandTotal.toFixed(2),
      numericGrandTotal: roundedGrandTotal,
    };
  };

  const totals = calculateTotals();

  const handleSave = async () => {
    // Validation
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const mrpNum = parseFloat(String(item.mrp));
      const discountNum = parseFloat(String(item.productDiscount));

      if (isNaN(mrpNum) || mrpNum < 0) {
        toast.error(`Please enter a valid MRP for "${item.productName}"`);
        return;
      }

      if (isNaN(discountNum) || discountNum < 0 || discountNum > 100) {
        toast.error(
          `Discount for "${item.productName}" must be between 0% and 100%`
        );
        return;
      }
    }

    try {
      setIsSubmitting(true);
      setErrorMessage(null);

      const productsPayload: UpdateOfflineProductItemInput[] = items.map(
        (item) => ({
          productId: item.productId,
          noOfItems: Number(item.productsItemsCount),
          inwardStockId: item.inwardStockId,
          productDiscount: parseFloat(String(item.productDiscount)) || 0,
          mrp: parseFloat(String(item.mrp)) || 0,
        })
      );

      const updatePayload: UpdateOfflineOrderPayload = {
        totalAmount: totals.numericGrandTotal,
        discount: parseFloat(totals.totalDiscount),
        products: productsPayload,
      };

      const response = await OfflineItemService.updateOfflineOrder(
        order.orderId,
        updatePayload
      );

      if (response && response.success !== false) {
        toast.success("Order updated successfully!");
        onClose();
        onSuccess(order.orderId);
      } else {
        const msg = response?.message || "Failed to update offline order";
        setErrorMessage(msg);
        toast.error(msg);
      }
    } catch (err: any) {
      console.error("Error updating offline order:", err);
      const msg = err?.message || "Error updating order. Please try again.";
      setErrorMessage(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[92vh] flex flex-col overflow-hidden animate-fadeIn">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-gray-800">
                Edit Offline Order
              </h2>
              <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded">
                Invoice #{order.invoiceNumber || order.orderId}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Patient: <span className="font-medium text-gray-700">{order.patientName}</span>
              {order.doctorName && (
                <span> | Doctor: <span className="font-medium text-gray-700">{order.doctorName}</span></span>
              )}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Modal Body / Products list */}
        <div
          className="p-6 overflow-y-auto flex-1"
          style={{ scrollbarWidth: "thin" }}
        >
          {errorMessage && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-sm text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
              Product Pricing & Discounts ({items.length} item{items.length !== 1 ? "s" : ""})
            </h3>
            <span className="text-xs text-gray-500 italic">
              * Note: Line item additions/deletions are restricted. Edit MRP and Discount percentage only.
            </span>
          </div>

          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">Product</th>
                  <th className="px-3 py-3 text-center font-semibold text-gray-600">Qty (Strips / Units)</th>
                  <th className="px-3 py-3 text-center font-semibold text-gray-600 w-36">MRP per Unit (₹)</th>
                  <th className="px-3 py-3 text-center font-semibold text-gray-600 w-32">Discount (%)</th>
                  <th className="px-3 py-3 text-center font-semibold text-gray-600">Price/Unit (₹)</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-600">Line Total (₹)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {items.map((item, index) => {
                  const mrpNum = parseFloat(String(item.mrp)) || 0;
                  const discountPercent = parseFloat(String(item.productDiscount)) || 0;
                  const qty = item.productsItemsCount || 1;
                  const pricePerUnit = mrpNum * (1 - discountPercent / 100);
                  const lineTotal = pricePerUnit * qty;

                  return (
                    <tr key={item.productId || index} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <img
                            src={item.productImage || defaultMedicineImg}
                            alt={item.productName}
                            className="w-10 h-10 object-cover rounded-lg border border-gray-200"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = defaultMedicineImg;
                            }}
                          />
                          <div>
                            <p className="font-medium text-gray-900">{item.productName}</p>
                            <p className="text-xs text-gray-500">
                              Pack: {item.tabletsPerStripe} tablets/strip
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-center text-gray-800 font-medium">
                        {item.productsItemsCount}
                      </td>
                      <td className="px-3 py-3 text-center">
                        <div className="relative rounded-md shadow-sm">
                          <span className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none text-gray-500 text-xs">
                            ₹
                          </span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.mrp}
                            onChange={(e) => handlePriceChange(index, e.target.value)}
                            className="block w-full pl-6 pr-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500 text-right"
                            placeholder="0.00"
                          />
                        </div>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <div className="relative rounded-md shadow-sm">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={item.productDiscount}
                            onChange={(e) => handleDiscountChange(index, e.target.value)}
                            className="block w-full pr-6 pl-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500 text-right"
                            placeholder="0"
                          />
                          <span className="absolute inset-y-0 right-0 pr-2.5 flex items-center pointer-events-none text-gray-500 text-xs">
                            %
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-center text-gray-700 font-medium">
                        ₹{pricePerUnit.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-900 font-bold">
                        ₹{lineTotal.toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Recalculated Summary Section */}
          <div className="mt-6 flex justify-end">
            <div className="w-80 bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2.5 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal (MRP Total):</span>
                <span className="font-medium">₹{totals.totalMrp}</span>
              </div>
              <div className="flex justify-between text-green-600">
                <span>Total Discount:</span>
                <span className="font-medium">- ₹{totals.totalDiscount}</span>
              </div>
              <div className="flex justify-between text-gray-500 text-xs">
                <span>Round Off:</span>
                <span>{Number(totals.roundOff) >= 0 ? `+₹${totals.roundOff}` : `-₹${Math.abs(Number(totals.roundOff)).toFixed(2)}`}</span>
              </div>
              <div className="border-t border-gray-200 pt-2 flex justify-between text-base font-bold text-gray-900">
                <span>Revised Total:</span>
                <span className="text-green-700">₹{totals.grandTotal}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-5 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSubmitting}
            className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors shadow flex items-center gap-2 disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Updating Order...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
